import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { isValidBIC, isValidIBAN } from 'ibantools';
import { bankgiroValidator } from './bankgiro.validator';
import { plusgiroValidator } from './plusgiro.validator';
import { parseSwedishBankAccount } from './swedish-bank-account-adapter';
import { alphaNumericUpper, normalizeIban } from './validation.utils';

export interface BankAccountGroupValidatorConfig {
  swedishTypeValue: string | number;
  ibanTypeValue: string | number;
  otherInternationalTypeValue: string | number;
  plusgiroTypeValue: string | number;
  bankgiroTypeValue: string | number;
}

type GroupValidationErrors = ValidationErrors | null;

function normalizeComparableValue(value: string | number | null | undefined): string {
  return value === null || value === undefined ? '' : String(value);
}

function sameTypeValue(
  actual: string | number | null | undefined,
  expected: string | number
): boolean {
  return normalizeComparableValue(actual) === normalizeComparableValue(expected);
}

function mergeControlErrors(
  control: AbstractControl | null,
  errorsToAdd: ValidationErrors
): void {
  if (!control) {
    return;
  }

  control.setErrors({
    ...(control.errors ?? {}),
    ...errorsToAdd
  });
}

function clearControlErrorKeys(
  control: AbstractControl | null,
  keys: string[]
): void {
  if (!control?.errors) {
    return;
  }

  const nextErrors: ValidationErrors = { ...control.errors };

  for (const key of keys) {
    delete nextErrors[key];
  }

  control.setErrors(Object.keys(nextErrors).length ? nextErrors : null);
}

function clearBankAccountValidationErrors(control: AbstractControl): void {
  clearControlErrorKeys(control.get('clearing'), [
    'invalidClearing',
    'invalidClearingMessageKey'
  ]);

  clearControlErrorKeys(control.get('accountNumber'), [
    'invalidAccountNumber',
    'invalidAccountNumberMessageKey',
    'invalidSwedishBankAccount',
    'invalidSwedishBankAccountMessageKey',
    'invalidPlusgiro',
    'invalidPlusgiroMessageKey',
    'invalidBankgiro',
    'invalidBankgiroMessageKey'
  ]);

  clearControlErrorKeys(control.get('bankName'), [
    'bankNameMismatch',
    'bankNameMismatchMessageKey',
    'expectedBankName'
  ]);

  clearControlErrorKeys(control.get('iban'), [
    'invalidIban',
    'invalidIbanMessageKey'
  ]);

  clearControlErrorKeys(control.get('bic'), [
    'invalidBic',
    'invalidBicMessageKey',
    'bicCountryMismatch',
    'bicCountryMismatchMessageKey'
  ]);
}

export function bankAccountGroupValidator(
  config: BankAccountGroupValidatorConfig
): ValidatorFn {
  return (control: AbstractControl): GroupValidationErrors => {
    const accountType = control.get('accountType')?.value;

    const clearingControl = control.get('clearing');
    const accountNumberControl = control.get('accountNumber');
    const bankNameControl = control.get('bankName');
    const ibanControl = control.get('iban');
    const bicControl = control.get('bic');

    const clearing = (clearingControl?.value ?? '').toString().trim();
    const accountNumber = (accountNumberControl?.value ?? '').toString().trim();
    const bankName = (bankNameControl?.value ?? '').toString().trim();
    const iban = normalizeIban((ibanControl?.value ?? '').toString());
    const bic = alphaNumericUpper((bicControl?.value ?? '').toString());

    clearBankAccountValidationErrors(control);

    if (accountType === null || accountType === undefined || accountType === '') {
      return null;
    }

    if (sameTypeValue(accountType, config.swedishTypeValue)) {
      if (!clearing && !accountNumber && !bankName && !iban && !bic) {
        return null;
      }

      if (!clearing) {
        mergeControlErrors(clearingControl, {
          invalidClearing: true,
          invalidClearingMessageKey: 'validation.invalid-clearing-default'
        });

        return { invalidSwedishAccount: true };
      }

      const clearingDigits = clearing.replace(/\D/g, '');
      if (clearingDigits.length < 4 || clearingDigits.length > 5) {
        mergeControlErrors(clearingControl, {
          invalidClearing: true,
          invalidClearingMessageKey: 'validation.invalid-clearing-default'
        });

        return { invalidSwedishAccount: true };
      }

      if (!accountNumber) {
        mergeControlErrors(accountNumberControl, {
          invalidAccountNumber: true,
          invalidAccountNumberMessageKey: 'validation.invalid-account-number-default'
        });

        return { invalidSwedishAccount: true };
      }

      const parsed = parseSwedishBankAccount(clearing, accountNumber);

      if (!parsed.isValid) {
        mergeControlErrors(accountNumberControl, {
          invalidSwedishBankAccount: true,
          invalidSwedishBankAccountMessageKey: 'validation.invalid-account-number-default'
        });

        return { invalidSwedishAccount: true };
      }

      if (
        bankName &&
        parsed.bankName &&
        bankName.trim().toLowerCase() !== parsed.bankName.trim().toLowerCase()
      ) {
        mergeControlErrors(bankNameControl, {
          bankNameMismatch: true,
          bankNameMismatchMessageKey: 'validation.bank-name-mismatch',
          expectedBankName: parsed.bankName
        });

        return { bankNameMismatch: true };
      }

      return null;
    }

    if (sameTypeValue(accountType, config.ibanTypeValue)) {
      const errors: ValidationErrors = {};

      const ibanIsValid = !iban || isValidIBAN(iban);
      const bicIsValid = !bic || isValidBIC(bic);

      if (iban && !ibanIsValid) {
        mergeControlErrors(ibanControl, {
          invalidIban: true,
          invalidIbanMessageKey: 'validation.invalid-iban-default'
        });

        errors.invalidIban = true;
      }

      if (bic && !bicIsValid) {
        mergeControlErrors(bicControl, {
          invalidBic: true,
          invalidBicMessageKey: 'validation.invalid-bic-default'
        });

        errors.invalidBic = true;
      }

      if (iban && bic && ibanIsValid && bicIsValid) {
        const ibanCountry = iban.substring(0, 2).toUpperCase();
        const bicCountry = bic.substring(4, 6).toUpperCase();

        if (ibanCountry !== bicCountry) {
          mergeControlErrors(bicControl, {
            bicCountryMismatch: true,
            bicCountryMismatchMessageKey: 'validation.bic-country-mismatch',
            ibanCountry,
            bicCountry
          });

          errors.bicCountryMismatch = true;
        }
      }

      return Object.keys(errors).length ? errors : null;
    }

    if (sameTypeValue(accountType, config.otherInternationalTypeValue)) {
      if (!bic) {
        return null;
      }

      if (!isValidBIC(bic)) {
        mergeControlErrors(bicControl, {
          invalidBic: true,
          invalidBicMessageKey: 'validation.invalid-bic-default'
        });

        return { invalidBic: true };
      }

      return null;
    }

    if (sameTypeValue(accountType, config.plusgiroTypeValue)) {
      const result = plusgiroValidator(accountNumberControl as AbstractControl);

      if (result) {
        mergeControlErrors(accountNumberControl, {
          ...result,
          invalidPlusgiro: true,
          invalidPlusgiroMessageKey: 'help.plusgiro'
        });

        return { invalidPlusgiro: true };
      }

      return null;
    }

    if (sameTypeValue(accountType, config.bankgiroTypeValue)) {
      const result = bankgiroValidator(accountNumberControl as AbstractControl);

      if (result) {
        mergeControlErrors(accountNumberControl, {
          ...result,
          invalidBankgiro: true,
          invalidBankgiroMessageKey: 'help.bankgiro'
        });

        return { invalidBankgiro: true };
      }

      return null;
    }

    return null;
  };
}